import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { FeatherTrash2 } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useRemoveInstanceEvidenceCommandControllerRemove } from "@/generated/compliance-tasks/compliance-tasks";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";

interface RemoveEvidenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instanceId: string;
  fileId: string;
  fileName: string;
}

export function RemoveEvidenceDialog({
  open,
  onOpenChange,
  instanceId,
  fileId,
  fileName,
}: RemoveEvidenceDialogProps) {
  const queryClient = useQueryClient();
  const removeEvidence = useRemoveInstanceEvidenceCommandControllerRemove();
  // Covers the whole confirm flow (mutation + refetches) so the button can't
  // fire twice while the dialog is still visibly open.
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await removeEvidence.mutateAsync({ instanceId, fileId });
      toast.success("Evidence file removed");
      await invalidateComplianceQueries(queryClient);
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(error, "Failed to remove the evidence file"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">Remove evidence file</DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-6">
              <IconWithLogoBackground
                variant="error"
                size="x-large"
                icon={<FeatherTrash2 />}
              />
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                  Remove this file?
                </span>
                <span className="text-center text-body font-body text-subtext-color">
                  <strong>{fileName}</strong> will be permanently removed from
                  this task. Upload the correct certificate afterwards if this
                  is a replacement.
                </span>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="destructive-primary"
              size="extra-large"
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy ? "Removing..." : "Yes, remove"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
